/* eslint-disable import/no-cycle */
import { defineStore } from 'pinia';
import {
  ISpacesContact,
  IRequestSpacesContact,
  IGroup,
  IResponse,
  IData,
  ISearch,
  IRequestSpaces,
  IRequestSpacesGroup,
  IGroupData,
} from '@/types/interfaces';
import SpacesService from '../services/SpacesService';
import { useMainStore } from '@/store/mainStore';
import { ACCOUNT_STATUS, CONTACT_STATUS } from '@/types/enums';
import { useAlertsStore } from '@/modules/alerts/store/alertsStore';

export type SpacesState = {
  suggestions: ISpacesContact[],
  invitationsContact: IRequestSpacesContact[],
  invitationsGroup: IRequestSpacesGroup[],
  invitationsAll: IRequestSpaces[],
  requestsToMyGroups: IRequestSpacesGroup[],
  outgoingContact: IRequestSpacesContact[],
  outgoingGroup: IGroupData[],
  requestsToGroup: IRequestSpacesGroup[],
  myContacts: IRequestSpacesContact[],
  searchContactsList: ISpacesContact[],
  searchGroupsList: IGroupData[],
  myOwnGroups: IGroup[], // TODO refactor
  myGroups: IGroupData[],
  usersToAdd: string[],
  search: string,
  groupsByOwnerId: IGroup[],
  participatedGroups: IGroup[],
  showBetaUserPopUp: boolean,
  dataLoading: boolean,
}

function requestContactWrapper(contact: ISpacesContact): IRequestSpacesContact {
  return { user: contact };
}

export const useSpacesStore = defineStore({
  id: 'spacesStore',

  state: () => ({
    invitationsContact: [],
    invitationsGroup: [],
    invitationsAll: [],
    requestsToMyGroups: [],
    outgoingContact: [],
    outgoingGroup: [],
    requestsToGroup: [],
    suggestions: [],
    myContacts: [],
    searchContactsList: [],
    searchGroupsList: [],
    myOwnGroups: [],
    myGroups: [],
    usersToAdd: [],
    groupContacts: [],
    search: '',
    dataLoading: false,
    groupsByOwnerId: [],
    participatedGroups: [],
    showBetaUserPopUp: false,
  } as SpacesState),

  getters: {
    filteredMyContacts(): IRequestSpacesContact[] {
      return this.myContacts.filter(
        (cont) => `${cont.user.firstName.toLocaleLowerCase()} ${cont.user.lastName.toLocaleLowerCase()}`.includes(this.search.toLocaleLowerCase()),
      );
    },
    filteredOutgoingContact(): IRequestSpacesContact[] {
      return this.outgoingContact.filter(
        (cont) => `${cont.user.firstName.toLocaleLowerCase()} ${cont.user.lastName.toLocaleLowerCase()}`.includes(this.search.toLocaleLowerCase()),
      );
    },
    filteredOutgoingGroup(): IGroupData[] {
      return this.outgoingGroup.filter(
        (group) => group.name.toLocaleLowerCase().includes(this.search.toLocaleLowerCase()),
      );
    },
    filteredInvitationsContact(): IRequestSpacesContact[] {
      return this.invitationsContact.filter(
        (cont) => `${cont.user.firstName.toLocaleLowerCase()} ${cont.user.lastName.toLocaleLowerCase()}`.includes(this.search.toLocaleLowerCase()),
      );
    },
    filteredInvitationsGroup(): IRequestSpacesGroup[] {
      return this.invitationsGroup.filter(
        (cont) => cont.group.name.toLocaleLowerCase().includes(this.search.toLocaleLowerCase()),
      );
    },
    filteredInvitationsAll(): IRequestSpaces[] {
      return this.invitationsAll.filter((cont) => {
        if (cont.user) {
          return `${cont.user.firstName.toLocaleLowerCase()} ${cont.user.lastName.toLocaleLowerCase()}`.includes(this.search.toLocaleLowerCase());
        }
        if (cont.group) {
          return cont.group.name.toLocaleLowerCase().includes(this.search.toLocaleLowerCase());
        }
        return [];
      });
    },
    filteredRequestsToMyGroups(): IRequestSpacesGroup[] {
      return this.requestsToMyGroups.filter(
        (cont) => cont.group.name.toLocaleLowerCase().includes(this.search.toLocaleLowerCase()),
      );
    },
    filteredSuggestions(): ISpacesContact[] {
      return this.suggestions.filter(
        (cont) => `${cont.firstName.toLocaleLowerCase()} ${cont.lastName.toLocaleLowerCase()}`.includes(this.search.toLocaleLowerCase()),
      );
    },
    filteredByNameGroupsByOwnerId(): IGroup[] {
      return this.groupsByOwnerId.filter(
        (group) => group.name.toLocaleLowerCase().includes(this.search.toLocaleLowerCase()),
      );
    },
    filteredByNameParticipatedGroups(): IGroup[] {
      return this.participatedGroups.filter(
        (group) => group.name.toLocaleLowerCase().includes(this.search.toLocaleLowerCase()),
      );
    },
  },

  actions: {
    async fetchInvitationsContact(): Promise<IRequestSpacesContact[]> {
      return SpacesService.fetchInvitationsContact()
        .then((response) => {
          this.invitationsContact = response.results.data;
          return this.invitationsContact;
        });
    },

    async fetchInvitationsGroup(): Promise<IRequestSpacesGroup[]> {
      return SpacesService.fetchInvitationsGroup()
        .then((response) => {
          this.invitationsGroup = response.results.data;
          return this.invitationsGroup;
        });
    },

    async fetchInvitationsAll(): Promise<IRequestSpaces[]> {
      return SpacesService.fetchInvitationsAll()
        .then((response) => {
          this.invitationsAll = response.results.data;
          return this.invitationsAll;
        });
    },

    async fetchRequestsToMyGroups(): Promise<IRequestSpacesGroup[]> {
      return SpacesService.fetchRequestsToMyGroups()
        .then((response) => {
          this.requestsToMyGroups = response.results.data;
          return this.requestsToMyGroups;
        });
    },

    async fetchOutgoingContact(): Promise<IRequestSpacesContact[]> {
      return SpacesService.fetchOutgoingContact()
        .then((response) => {
          this.outgoingContact = response.results.data;

          return this.outgoingContact;
        });
    },

    async fetchOutgoingGroup(): Promise<IGroupData[]> {
      return SpacesService.fetchOutgoingGroup()
        .then((response) => {
          this.outgoingGroup = response.results.data;

          return this.outgoingGroup;
        });
    },

    async fetchRequestsToGroup(groupId: string): Promise<IRequestSpacesGroup[]> {
      return SpacesService.fetchRequestsToGroup(groupId)
        .then((response) => {
          this.requestsToGroup = response.results.data;

          return this.requestsToGroup;
        });
    },

    async fetchSuggestions(): Promise<ISpacesContact[]> {
      return SpacesService.fetchSuggestions()
        .then((response) => {
          this.suggestions = response.results.data.filter((item) => item);

          return this.suggestions;
        });
    },

    async fetchMyContacts(useCache = false): Promise<IRequestSpacesContact[]> {
      if (useCache && this.myContacts.length) {
        return this.myContacts;
      }
      return SpacesService.fetchMyContacts()
        .then((response) => {
          this.myContacts = response.results.data;

          return this.myContacts;
        });
    },

    async searchContacts(query: string, pageIndex: number, pageSize: number, push?: boolean): Promise<IResponse<IData<ISearch<ISpacesContact[]>>>> {
      this.dataLoading = true;
      return SpacesService.searchContact(query, pageIndex, pageSize).then((response) => {
        if (response.success) {
          if (push) {
            this.searchContactsList.push(...response.results.data.content);
          } else {
            this.searchContactsList = response.results.data.content;
          }
        } else {
          this.searchContactsList = [];
        }
        this.dataLoading = false;
        return response;
      });
    },

    async searchGroups(query: string, push?: boolean): Promise<IResponse<IData<IGroupData[]>>> {
      this.dataLoading = true;
      return SpacesService.searchGroups(query).then((response) => {
        if (response.success) {
          this.searchGroupsList = response.results.data;
        } else {
          this.searchGroupsList = [];
        }
        this.dataLoading = false;
        return response;
      });
    },

    changeSearchContactStatus(contactId: string, contactStatus: CONTACT_STATUS) {
      this.searchContactsList = this.searchContactsList.map((con) => {
        if (con.accountId === contactId) {
          return { ...con, contactStatus };
        }
        return con;
      });
    },

    changeSearchGroupStatus(groupId: string, groupStatus: CONTACT_STATUS) {
      this.searchGroupsList = this.searchGroupsList.map((group) => {
        if (group.id === groupId) {
          console.log('{ ...group, groupStatus }', { ...group, groupStatus });
          return { ...group, groupStatus };
        }
        return group;
      });
    },

    acceptContact(contactId: string): void {
      const alertsStore = useAlertsStore();
      SpacesService.acceptContact(contactId).then(() => {
        alertsStore.fetchNotifications(true);
      });
      const currentContact = this.invitationsContact.find((contact) => contact.user?.accountId === contactId);
      if (currentContact) {
        this.myContacts.unshift(currentContact as IRequestSpacesContact);
      }
      this.invitationsContact = this.invitationsContact.filter((contact) => contact.user?.accountId !== contactId);

      this.changeSearchContactStatus(contactId, CONTACT_STATUS.CONTACT);
    },

    acceptRequestToGroup(groupId: string, partnerId: string): void {
      const alertsStore = useAlertsStore();
      SpacesService.acceptRequestToGroup(groupId, partnerId).then(() => {
        alertsStore.fetchNotifications(true);
      });
      const currentGroup = this.invitationsGroup.find((contact) => contact.group?.id === groupId);
      this.myGroups = this.myGroups.filter((group) => group.id !== groupId);

      if (currentGroup) {
        this.myGroups.unshift(currentGroup.group as IGroupData);
      }
      this.invitationsGroup = this.invitationsGroup.filter((contact) => contact.group?.id !== groupId);

      // this.changeSearchContactStatus(contactId, CONTACT_STATUS.CONTACT);
    },

    acceptUserToGroup(groupId: string, partnerId: string): void {
      const alertsStore = useAlertsStore();
      SpacesService.acceptUserToGroup(groupId, partnerId).then(() => {
        alertsStore.fetchNotifications(true);
      });

      this.requestsToMyGroups = this.requestsToMyGroups.filter((request) => request.group?.id !== groupId || request.partner?.accountId !== partnerId);

      // this.changeSearchContactStatus(contactId, CONTACT_STATUS.CONTACT);
    },

    rejectContact(contactId: string): void {
      const alertsStore = useAlertsStore();
      SpacesService.rejectContact(contactId).then(() => {
        alertsStore.fetchNotifications(true);
      });
      this.invitationsContact = this.invitationsContact.filter((contact) => contact.user?.accountId !== contactId);

      this.changeSearchContactStatus(contactId, CONTACT_STATUS.NO_CONTACT);
    },

    rejectRequestToGroup(groupId: string, partnerId: string): void {
      const alertsStore = useAlertsStore();
      SpacesService.rejectRequestToGroup(groupId, partnerId).then(() => {
        alertsStore.fetchNotifications(true);
      });
      this.invitationsGroup = this.invitationsGroup.filter((contact) => contact.group?.id !== groupId);
    },

    rejectUserToGroup(groupId: string, partnerId: string): void {
      const alertsStore = useAlertsStore();
      SpacesService.rejectUserToGroup(groupId, partnerId).then(() => {
        alertsStore.fetchNotifications(true);
      });
      this.requestsToMyGroups = this.requestsToMyGroups.filter((request) => request.group?.id !== groupId || request.partner?.accountId !== partnerId);
    },

    async requestContact(contactId: string): Promise<void> {
      const mainStore = useMainStore();

      if (mainStore.user.accountStatuses.includes(ACCOUNT_STATUS.BETA)) {
        this.showBetaUserPopUp = true;
        return Promise.reject();
      }

      this.changeSearchContactStatus(contactId, CONTACT_STATUS.REQUEST_SENT);
      await SpacesService.requestContact(contactId);
      let currentContact;
      if (this.searchContactsList.length) {
        currentContact = this.searchContactsList.find((contact) => contact.accountId === contactId);
      } else {
        currentContact = this.suggestions.find((contact) => contact.accountId === contactId);
      }
      if (currentContact) {
        this.outgoingContact.push(requestContactWrapper(currentContact));
      }
      this.suggestions = this.suggestions.filter((contact) => contact.accountId !== contactId);

      return Promise.resolve();
    },

    async requestToJoinGroup(groupId: string): Promise<void> {
      const mainStore = useMainStore();

      if (mainStore.user.accountStatuses.includes(ACCOUNT_STATUS.BETA)) {
        this.showBetaUserPopUp = true;
        return Promise.reject();
      }

      this.changeSearchGroupStatus(groupId, CONTACT_STATUS.PENDING);
      await SpacesService.requestToJoinGroup(groupId);
      const currentGroups = this.searchGroupsList.find((group) => group.id === groupId);
      if (currentGroups) {
        this.outgoingGroup.push(currentGroups);
      }

      return Promise.resolve();
    },

    deleteContact(contactId: string): void {
      SpacesService.deleteContact(contactId);
      this.myContacts = this.myContacts.filter((myContact) => myContact.user.accountId !== contactId);
    },

    async fetchMyOwnGroups(useCache = false): Promise<IGroup[]> { // TODO refactor
      if (useCache && this.myOwnGroups.length) {
        return this.myOwnGroups;
      }
      const mainStore = useMainStore();
      return SpacesService.fetchGroupsByOwnerId(mainStore.user.accountId)
        .then((response) => {
          if (response.success) {
            this.myOwnGroups = response.results.data;
          }
          return this.myOwnGroups;
        });
    },

    async fetchMyGroups(): Promise<IGroup[]> {
      return SpacesService.fetchAllGroups()
        .then((response) => {
          if (response.success) {
            this.myGroups = response.results.data;
          }
          return this.myGroups;
        });
    },

    async fetchUserParticipatedGroups() :Promise<IGroup[]> {
      const mainStore = useMainStore();
      const userId = mainStore.user.accountId;
      return SpacesService.fetchParticipatedGroupsByUser(userId)
        .then((response) => {
          if (response.success) {
            this.participatedGroups = response.results.data;
          }
          // shortList.value = userGroups.value.slice(0, 2); // this can be moved inside SpacesGroupDesc (не совсем понятно название компонента)
          return this.participatedGroups;
        });
    },
    async fetchUsersGroups() :Promise<IGroup[]> {
      const mainStore = useMainStore();
      const userId = mainStore.user.accountId;
      return SpacesService.fetchGroupsByOwnerId(userId)
        .then((response) => {
          if (response.success) {
            this.groupsByOwnerId = response.results.data;
          }
          return this.groupsByOwnerId;
        });
    },
  },

});
